/** Client-side FiveM blip snippet generator. Values are treated as data, never source. */
export interface BlipSnippetOptions {
  sprite: number;
  colour: number;
  name: string;
  x: number;
  y: number;
  z: number;
  scale: number;
  shortRange: boolean;
  route: boolean;
}
export type BlipSnippetLanguage = "lua" | "javascript";

function numeric(value: number): string {
  if (!Number.isFinite(value)) throw new Error("Blip coordinates and settings must be finite numbers");
  return String(value);
}

/** Lua supports decimal byte escapes (\ddd), not JavaScript's \uXXXX escape syntax. */
export function luaString(value: string): string {
  return '"' + value.replace(/["\\\u0000-\u001f\u007f]/g, (character) => {
    if (character === '"') return '\\"';
    if (character === "\\") return "\\\\";
    return "\\" + character.charCodeAt(0).toString(10).padStart(3, "0");
  }) + '"';
}

export function buildBlipSnippet(options: BlipSnippetOptions, language: BlipSnippetLanguage): string {
  const { sprite, colour, name, x, y, z, scale, shortRange, route } = options;
  if (!Number.isInteger(sprite) || sprite < 0 || !Number.isInteger(colour) || colour < 0) {
    throw new Error("A valid sprite and colour ID are required");
  }
  if (scale < 0.1 || scale > 2) throw new Error("Blip scale must be between 0.1 and 2");
  if (name.length > 80) throw new Error("Blip labels must be 80 characters or fewer");
  const coords = [numeric(x), numeric(y), numeric(z)];
  const label = language === "lua" ? luaString(name) : JSON.stringify(name);
  if (language === "lua") {
    return [
      "-- client.lua · Run this in a client script",
      "local blip = AddBlipForCoord(" + coords.join(", ") + ")",
      "SetBlipSprite(blip, " + sprite + ")",
      "SetBlipColour(blip, " + colour + ")",
      "SetBlipScale(blip, " + numeric(scale) + ")",
      "SetBlipDisplay(blip, 4)",
      "SetBlipAsShortRange(blip, " + String(shortRange) + ")",
      ...(route ? ["SetBlipRoute(blip, true)"] : []),
      "BeginTextCommandSetBlipName(\"STRING\")",
      "AddTextComponentString(" + label + ")",
      "EndTextCommandSetBlipName(blip)",
      "",
      "-- Remove only the blip owned by this resource when it stops.",
      "AddEventHandler('onResourceStop', function(resourceName)",
      "    if resourceName == GetCurrentResourceName() and DoesBlipExist(blip) then",
      "        RemoveBlip(blip)",
      "    end",
      "end)",
    ].join("\n");
  }
  return [
    "// client.js · Run this in a client script",
    "const blip = AddBlipForCoord(" + coords.join(", ") + ");",
    "SetBlipSprite(blip, " + sprite + ");",
    "SetBlipColour(blip, " + colour + ");",
    "SetBlipScale(blip, " + numeric(scale) + ");",
    "SetBlipDisplay(blip, 4);",
    "SetBlipAsShortRange(blip, " + String(shortRange) + ");",
    ...(route ? ["SetBlipRoute(blip, true);"] : []),
    "BeginTextCommandSetBlipName(\"STRING\");",
    "AddTextComponentString(" + label + ");",
    "EndTextCommandSetBlipName(blip);",
    "",
    "// Remove only the blip owned by this resource when it stops.",
    "on('onResourceStop', (resourceName) => {",
    "  if (resourceName === GetCurrentResourceName() && DoesBlipExist(blip)) {",
    "    RemoveBlip(blip);",
    "  }",
    "});",
  ].join("\n");
}
